import "./spinWinModal.css";
import { routes } from "../../routes";
import Mascot from "../../assets/images/Mascot.png";
import { useDispatch, useSelector } from "react-redux";
// import ShoppingCart from "../../assets/icons/cart.svg";
import { updateUserData } from "../../redux/slices/authSlice";
import { syncUserData } from "../../services/userSync.service";
import { useCallback, useEffect, useState, useRef } from "react";
import SpinBackground from "../../assets/images/spinBackground.png";
import { ReactComponent as ShoppingCart } from "../../assets/icons/cart.svg";
import { getAccountBalance, sellItem } from "../../api/modules/b2droppie.class";

const SpinWinModal = ({
  unbox,
  winner,
  navigate,
  showSpin,
  itemsData,
  setMessage,
  setShowSpin,
  setIsLoading,
  setCallbackData,
  setShowVerifyEmailModal,
}) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [hasFreeBox, setHasFreeBox] = useState(false);
  const user = useSelector((state) => state?.auth?.user);
  const [showSellConfirm, setShowSellConfirm] = useState(false);
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  const modalRef = useRef(null);
  const dragRef = useRef({
    isDragging: false,
    startY: 0,
    currentY: 0,
  });

  useEffect(() => {
    if (
      unbox?.hidden_from_shop &&
      user?.free_boxes &&
      user?.free_boxes_remaining > 0
    ) {
      setHasFreeBox(true);
    }
    if (winner && itemsData?.length) {
      const matched = itemsData.find((item) => item.id === winner?.winnerId);
      if (matched) setData(matched);
    }
  }, [winner, itemsData]);

  useEffect(() => {
    if (showSpin) {
      document.body.classList.add("modal-open");
      requestAnimationFrame(() => setIsActive(true));
    } else {
      setIsActive(false);
      document.body.classList.remove("modal-open");
    }

    return () => document.body.classList.remove("modal-open");
  }, [showSpin]);

  const handleClose = useCallback(() => {
    setIsActive(false);

    setTimeout(() => {
      if (hasFreeBox) {
        navigate(`${routes.ACCOUNT}/${routes.ACCOUNT_FREEBOXES}`);
      } else if (unbox?.hidden_from_shop) {
        navigate(routes.HOME, { replace: true });
      } else {
        setShowSpin(false);
      }
    }, 300);
  }, [navigate, setShowSpin, unbox, hasFreeBox]);

  const startDrag = (clientY) => {
    dragRef.current.isDragging = true;
    dragRef.current.startY = clientY;
    dragRef.current.currentY = 0;
    modalRef.current?.classList.add("dragging");
  };

  const moveDrag = (clientY) => {
    if (!dragRef.current.isDragging) return;
    const delta = clientY - dragRef.current.startY;
    const translateY = Math.max(0, delta);
    dragRef.current.currentY = translateY;
    modalRef.current.style.transform = `translateY(${translateY}px)`;
  };

  const endDrag = () => {
    if (!dragRef.current.isDragging) return;

    dragRef.current.isDragging = false;
    modalRef.current?.classList.remove("dragging");

    const shouldClose = dragRef.current.currentY > 120;

    if (shouldClose) {
      handleClose();
    } else {
      modalRef.current.style.transition = "transform 0.25s ease";
      modalRef.current.style.transform = "translateY(0)";

      setTimeout(() => {
        modalRef.current.style.transition = "";
      }, 250);
    }

    dragRef.current.currentY = 0;
  };

  const onTouchStart = (e) => startDrag(e.touches[0].clientY);
  const onTouchMove = (e) => moveDrag(e.touches[0].clientY);
  const onTouchEnd = endDrag;

  useEffect(() => {
    if (!showSpin) return;

    const onMouseMove = (e) => moveDrag(e.clientY);
    const onMouseUp = () => endDrag();

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [showSpin]);

  useEffect(() => {
    if (!showSpin) return;
    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [showSpin, handleClose]);

  const handleSellItem = async () => {
    setShowSellConfirm(false);
    setIsLoading(true);
    const product_id = winner?.winnerId || data?.id;

    try {
      setShowSpin(false);
      const response = await sellItem({
        product_id: [product_id],
        quantities: {
          [product_id]: 1,
        },
      });

      if (response?.data?.success) {
        await getAccountBalance();

        const updated = await syncUserData();
        if (updated) {
          dispatch(updateUserData(updated));
        }

        setCallbackData({
          title: "Sell Successful!",
          subtitle: "Your item has been sold successfully.",
          type: "success",
        });
      } else {
        setCallbackData({
          title: "Sell Failed",
          subtitle: response?.data?.error || "Please try again later.",
          type: "error",
        });
      }
    } catch (error) {
      if (error?.status === 403) {
        setShowVerifyEmailModal(true);
      }
      setMessage({
        success: "",
        error:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Something went wrong",
      });
      // setCallbackData({
      //   title: "Sell Failed",
      //   subtitle: "An error occurred while selling the item.",
      //   type: "error",
      // });
    } finally {
      setIsLoading(false);
    }
  };

  if (!showSpin) return null;

  return (
    <>
      <div
        className={`prz-modal-overlay ${isActive ? "active" : ""} ${isDarkMode ? "dark" : "light"}`}
        onClick={handleClose}
      >
        <div
          ref={modalRef}
          className="prz-modal-dialog"
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundImage: `url(${SpinBackground})`,
            backgroundSize: "cover",
          }}
        >
          <div className="prz-spinmodal-body">
            {/* ===== DRAG HANDLE (MOBILE) ===== */}
            <div
              className="prz-drag-handle"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onMouseDown={(e) => startDrag(e.clientY)}
            >
              <div className="prz-drag-indicator" />
            </div>

            {/* ===== HEADER ===== */}
            <div className="prz-congrats-header d-flex align-items-center gap-3 px-4">
              <img
                src={Mascot}
                alt="Droppie Mascot"
                style={{ width: "90px", height: "auto", objectFit: "contain" }}
              />
              <div className="d-flex flex-column align-items-start">
                <h2 className="prz-congrats-title mb-0">Congratulations!</h2>
                <p className="prz-winner-text mb-0">You're a winner</p>
              </div>
            </div>

            {/* ===== CONTENT ===== */}
            <div className="prz-spinmodal-content">
              <div className="prz-product-card d-center flex-column">
                <img
                  className="prz-product-image"
                  alt={data?.name || "Product"}
                  src={data?.image || "https://via.placeholder.com/200"}
                />
              </div>

              <div className="d-center mt-4 gap-2 w-95">
                <button
                  className="filled-medium-deep-yellow-btn-dark px-3 d-center gap-10px"
                  onClick={() => {
                    hasFreeBox
                      ? navigate(
                          `${routes.ACCOUNT}/${routes.ACCOUNT_FREEBOXES}`,
                        )
                      : navigate(routes.CART);
                  }}
                >
                  {hasFreeBox ? (
                    "More Free Boxes"
                  ) : (
                    <>
                      <ShoppingCart className="icon-color-black" />
                      Go to Cart
                    </>
                  )}
                </button>
                <button
                  className="deep-primary-monochrome-btn-dark"
                  onClick={() => setShowSellConfirm(true)}
                >
                  Sell this Item
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSellConfirm && (
        <div className={`confirm-overlay ${isDarkMode ? "dark" : "light"}`}>
          <div className="confirm-card">
            <div className="d-center">
              <h2>Sell Item?</h2>
            </div>

            <p className="d-center mt-2 mb-4">
              Are you sure you want to sell this item?
            </p>

            <div className="confirm-actions">
              <button
                className="confirm-btn cancel"
                onClick={() => {
                  setShowSellConfirm(false);
                }}
              >
                Cancel
              </button>

              <button
                className="confirm-btn yes"
                onClick={() => handleSellItem()}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SpinWinModal;
